/**
 * Spanish language keywords for category prediction
 */
import type {
  AccommodationSubcategoryKeywords,
  ContextKeywords,
  DrinksConfig,
  DrinksSubcategoryKeywords,
  EntertainmentSubcategoryKeywords,
  FoodConfig,
  FoodSubcategoryKeywords,
  HealthcareSubcategoryKeywords,
  LanguageKeywords,
  MLPatternKeywords,
  ShoppingContextKeywords,
  ShoppingSubcategoryKeywords,
  StemmingConfig,
  TransportConfig,
  TransportKeywords,
  TransportSubcategoryKeywords,
  UtilitiesSubcategoryKeywords,
} from '../types';

// Transport-related keywords for disambiguation
export const transportKeywords: TransportKeywords = {
  general: [
    'tren',
    'autobús',
    'autobus',
    'vuelo',
    'avión',
    'avion',
    'metro',
    'tranvía',
    'tranvia',
    'taxi',
    'transporte',
    'viaje',
    'aerolínea',
    'aerolinea',
    'ferrocarril',
    'autocar',
    'ferry',
    'barco',
    'coche',
    'auto',
    'trayecto',
    'aeropuerto',
    'estación',
    'estacion',
  ],
  tickets: ['boleto', 'boletos', 'pasaje', 'pasajes', 'billete', 'billetes'],
  tripContext: ['viaje', 'trayecto', 'vacaciones'],
};

// Transport subcategory keywords
export const transportSubcategories: TransportSubcategoryKeywords = {
  car: [
    'pago de auto',
    'pago del auto',
    'préstamo de auto',
    'lavado de auto',
    'lavado de coche',
    'detallado de auto',
    'seguro de auto',
    'seguro del coche',
    'vehículo',
  ],
  // ES common words only — brand names live in `../shared/brands.ts`.
  bus: [
    'autobús',
    'autobus',
    'camión',
    'guagua',
    'colectivo',
    'micro',
    'omnibus',
    'boleto de autobús',
    'pasaje de bus',
    'pasaje de autobús',
    'estación de autobuses',
  ],
  train: [
    'tren',
    'ferrocarril',
    'cercanías',
    'cercanias',
    'boleto de tren',
    'pasaje de tren',
    'billete de tren',
    'tren bala',
  ],
  tram: ['tranvía', 'tranvia', 'tren ligero', 'boleto de tranvía'],
  flight: [
    'vuelo',
    'aerolínea',
    'aerolinea',
    'avión',
    'avion',
    'boleto de avión',
    'pasaje de avión',
    'tarjeta de embarque',
  ],
  bike: ['bicicleta', 'bici', 'patinete', 'monopatín', 'monopatin', 'ciclismo'],
  boat: [
    'barco',
    'buque',
    'crucero',
    'navegación',
    'navegacion',
    'lancha',
    'yate',
    'embarcación',
    'embarcacion',
    'velero',
  ],
  parking: [
    'estacionamiento',
    'parqueo',
    'parquímetro',
    'parquimetro',
    'tarifa de estacionamiento',
    'aparcamiento',
  ],
  taxi: ['taxi', 'taxista', 'taxi amarillo', 'tarifa de taxi'],
  rideshare: ['viaje compartido'],
  metro: ['metro', 'subte', 'subterráneo', 'subterraneo', 'metropolitano'],
  fuel: [
    'gasolina',
    'gasoil',
    'combustible',
    'estación de servicio',
    'estacion de servicio',
    'gasolinera',
    'nafta',
    'diésel',
    'diesel',
  ],
  toll: ['peaje', 'caseta', 'caseta de cobro', 'autopista de peaje'],
  rental: [
    'renta de auto',
    'alquiler de coche',
    'alquiler de auto',
    'alquiler coche',
    'renta de coche',
  ],
  maintenance: [
    'cambio de aceite',
    'mecánico',
    'mecanico',
    'taller mecánico',
    'taller automotriz',
    'llanta',
    'llantas',
    'neumático',
    'neumatico',
    'frenos',
    'transmisión',
    'transmision',
    'afinación',
  ],
  airport: [
    'aeropuerto',
    'terminal aeropuerto',
    'estacionamiento aeropuerto',
    'transporte aeropuerto',
    'traslado aeropuerto',
  ],
};

// Only the generic word is shared by food and healthcare: on its own it
// cannot say sports nutrition or health, so the cloud tier breaks that
// tie. Specific products go to one parent so the free tier can answer.
const supplementsKeywords: string[] = ['suplementos', 'suplemento'];
const sportsNutritionKeywords: string[] = [
  'proteína',
  'proteina',
  'proteína en polvo',
  'whey',
  'creatina',
  'aminoácidos',
  'aminoacidos',
  'glutamina',
  'bcaa',
  'preentreno',
  'pre entreno',
];
const healthSupplementsKeywords: string[] = [
  'colágeno',
  'colageno',
  'vitaminas',
  'vitamina',
  'omega 3',
  'omega-3',
  'probióticos',
  'probioticos',
  'magnesio',
  'multivitamínico',
  'multivitaminico',
];

// Food subcategory keywords (partial list for demonstration)
export const foodSubcategories: Partial<FoodSubcategoryKeywords> = {
  // ES common words only — brand names + universal food terms live in
  // `../shared/brands.ts`.
  pizza: ['pizzería'],
  sushi: [],
  hamburger: ['hamburguesa', 'hamburgesa'],
  sandwich: ['sándwich', 'bocadillo', 'torta', 'submarino'],
  soup: [
    'sopa',
    'caldo',
    'potaje',
    'guiso',
    'estofado',
    'cocido',
    'gazpacho',
    'pozole',
    'sancocho',
  ],
  steak: [
    'bife',
    'churrasco',
    'lomo',
    'solomillo',
    'costilla',
    'parrilla',
    'asador',
    'entrecot',
  ],
  salad: [
    'ensalada',
    'ensalada césar',
    'ensalada griega',
    'ensalada mixta',
    'ensalada de la casa',
  ],
  pastry: [
    'pastelería',
    'repostería',
    'medialuna',
    'dona',
    'churros',
    'magdalena',
  ],
  dessert: ['postre', 'pastel', 'tarta', 'pudín'],
  candy: ['dulce', 'dulces', 'golosinas', 'chocolate', 'gomitas', 'chupetín'],
  icecream: ['helado', 'helados', 'sorbete', 'paleta'],
  snacks: [
    'aperitivo',
    'papas fritas',
    'palomitas',
    'frutos secos',
    'galletas saladas',
    'botana',
  ],
  fruit: [
    'fruta',
    'frutas',
    'manzana',
    'plátano',
    'platano',
    'naranja',
    'bayas',
  ],
  vegetables: ['verdura', 'verduras', 'vegetales', 'hortalizas'],
  meat: [
    'carnicería',
    'carne',
    'res',
    'cerdo',
    'cordero',
    'pollo',
    'pavo',
    'jamón',
    'tocino',
    'chorizo',
  ],
  seafood: [
    'mariscos',
    'pescado',
    'salmón',
    'atún',
    'camarón',
    'langosta',
    'cangrejo',
    'ostra',
    'pulpo',
    'gambas',
  ],
  dairy: [
    'lácteos',
    'leche',
    'queso',
    'yogur',
    'mantequilla',
    'crema',
    'requesón',
  ],
  breakfast: [
    'desayuno',
    'almuerzo',
    'pan tostado',
    'huevos',
    'tocino',
    'cereal',
  ],
  restaurant: [
    'restaurante',
    'comedor',
    'bistró',
    'asador',
    'parrilla',
    'italiano',
    'chino',
    'mexicano',
    'tailandés',
    'tailandes',
    'vietnamita',
    'coreano',
    'japonés',
    'japones',
    'peruano',
    'argentino',
    'colombiano',
    'patacones',
    'comida rápida',
    'comida rapida',
    'almuerzo',
    'comida',
    'mediodía',
    'mediodia',
    'cena',
  ],
  bakery: ['panadería', 'panaderia', 'pan'],
  // Venues only: in Spanish a bare 'café' is the drink (drinks/coffee).
  cafe: ['cafetería', 'cafeteria', 'coffee shop'],
  delivery: ['entrega', 'a domicilio', 'para llevar', 'pedido'],
  groceries: [
    'tienda de comestibles',
    'mercado',
    'bodega',
    'abarrotes',
    'despensa',
  ],
  supplements: [...sportsNutritionKeywords, ...supplementsKeywords],
};

export const drinksSubcategories: DrinksSubcategoryKeywords = {
  // ES common words only — brands live in `../shared/brands.ts`.
  // Bare "water" words stay out: they collide with utilities/water.
  drinks: ['bebida', 'bebidas'],
  water: [
    'agua mineral',
    'agua con gas',
    'agua sin gas',
    'botella de agua',
    'agua embotellada',
  ],
  juice: [
    'jugo',
    'jugos',
    'zumo',
    'zumos',
    'jugo de naranja',
    'zumo de naranja',
    'jugo natural',
    'malteada',
    'malteadas',
  ],
  soda: ['refresco', 'refrescos', 'gaseosa', 'gaseosas', 'limonada', 'soda'],
  // 'tinto' is the Colombian black coffee; 'vino tinto' still wins as
  // the longer wine phrase. Bare 'expreso' is left out: it names bus
  // lines (Expreso Brasilia).
  coffee: [
    'café',
    'cafe',
    'cafés',
    'cafecito',
    'tinto',
    'tintico',
    'café con leche',
    'café americano',
    'café expreso',
    'capuchino',
    'cortado',
    'café helado',
  ],
  tea: [
    'té',
    'té verde',
    'té helado',
    'infusión',
    'aromática',
    'yerba mate',
    'yerba',
  ],
  energy: [
    'bebida energética',
    'bebidas energéticas',
    'energizante',
    'bebida isotónica',
  ],
  beer: [
    'cerveza',
    'cervezas',
    'birra',
    'birras',
    'chela',
    'chelas',
    'pola',
    'polas',
    // Bare 'caña' is also a fishing rod ('caña de pescar').
    'una caña',
    'cañas',
    'cerveza artesanal',
  ],
  wine: [
    'vino',
    'vinos',
    'vino tinto',
    'vino blanco',
    'copa de vino',
    'botella de vino',
    'vinoteca',
    'tinto de verano',
  ],
  cocktails: [
    'cóctel',
    'coctel',
    'cócteles',
    'cocteles',
    'michelada',
    'micheladas',
    'carajillo',
    'calimocho',
  ],
  // Bare 'ginebra' is left out: it is also Geneva ('vuelo a Ginebra').
  spirits: [
    'ron',
    'aguardiente',
    'guaro',
    'fernet',
    'chupito',
    'chupitos',
    'licor de café',
  ],
  alcohol: [
    'alcohol',
    'licor',
    'licores',
    'licorería',
    'licoreria',
    'botillería',
    'bebidas alcohólicas',
  ],
  bar: [
    'bar',
    'pub',
    'cantina',
    'taberna',
    'cervecería',
    'cerveceria',
    'copas',
    'tragos',
  ],
};

// Context keywords
export const contextKeywords: ContextKeywords = {
  flight: ['equipaje', 'vuelo'],
  fuel: [
    'viaje',
    'trayecto',
    'vacaciones',
    'conducir',
    'auto',
    'carro',
    'coche',
    'kilómetros',
    'millas',
  ],
  transportTickets: [
    'boleto',
    'boletos',
    'pasaje',
    'pasajes',
    'billete',
    'billetes',
    'tarifa',
    'tránsito',
    'transporte',
    'viaje',
    'trayecto',
    'recorrido',
  ],
};

// Entertainment subcategory keywords
const entertainmentSubcategories: EntertainmentSubcategoryKeywords = {
  // ES common words only — brands + universal terms live in
  // `../shared/brands.ts`.
  music: [
    'concierto',
    'música',
    'musica',
    'álbum',
    'album',
    'canción',
    'cancion',
    'artista',
    'banda',
  ],
  streaming: ['suscripción', 'suscripcion', 'serie', 'capítulo'],
  gaming: ['juego', 'videojuego', 'consola', 'gamer'],
  books: [
    'libro',
    'revista',
    'periódico',
    'periodico',
    'biblioteca',
    'lectura',
    'novela',
    'literatura',
    'librería',
  ],
  fitness: ['gimnasio', 'entrenamiento', 'ejercicio', 'entrenador personal'],
  events: [
    'entrada',
    'boleto',
    'boleto evento',
    'evento',
    'teatro',
    'festival',
    'exposición',
    'exposicion',
    'concierto',
    'música en vivo',
    'festival de música',
    'presentación',
    'museo',
    'galería',
    'galería de arte',
    'centro cultural',
    'conferencia',
    'convención',
    'reunión',
    'encuentro',
    'feria',
    'carnaval',
    'celebración',
    'fiesta',
    'obra',
    'musical',
    'ópera',
    'vida nocturna',
    'club',
    'discoteca',
  ],
  sports: [
    'béisbol',
    'fútbol',
    'futbol',
    'baloncesto',
    'tenis',
    'estadio',
    'partido',
    'juego',
  ],
  photography: [
    'cámara',
    'camara',
    'foto',
    'fotografía',
    'fotografia',
    'lente',
    'sesión de fotos',
    'fotógrafo',
  ],
  podcasts: ['pódcast'],
  outdoor: [
    'senderismo',
    'campamento',
    'pesca',
    'caza',
    'aventura',
    'naturaleza',
    'montaña',
    'escalada',
    'trekking',
    'montañismo',
    'alpinismo',
    'vida salvaje',
    'parque',
    'parque de diversiones',
    'parque temático',
    'zoológico',
    'acuario',
  ],
  water: [
    'buceo',
    'esnórquel',
    'parapente',
    'playa',
    'natación',
    'deportes acuáticos',
    'tabla de paddle',
    'vela',
    'navegación',
    'esquí acuático',
    'acuático',
    'marino',
  ],
  movies: [
    'película',
    'pelicula',
    'cine',
    'proyección',
    'matiné',
    'sala de cine',
  ],
};

// Shopping subcategory keywords
const shoppingSubcategories: ShoppingSubcategoryKeywords = {
  // ES common words only — brands live in `../shared/brands.ts`.
  clothes: [
    'ropa',
    'vestimenta',
    'moda',
    'prenda',
    'camisa',
    'pantalón',
    'pantalon',
    'vestido',
    'chaqueta',
    'abrigo',
  ],
  electronics: [
    'electrónica',
    'electronica',
    'ordenador',
    'computadora',
    'teléfono',
    'telefono',
    'tableta',
    'auriculares',
    'electrodomésticos',
    'refrigerador',
    'lavadora',
    'secadora',
    'lavavajillas',
    'microondas',
    'horno',
  ],
  groceries: ['supermercado', 'despensa', 'abarrotes', 'comestibles'],
  online: ['compra online', 'compra en línea', 'envío', 'envio', 'paquete'],
  furniture: [
    'muebles',
    'silla',
    'mesa',
    'sofá',
    'sofa',
    'cama',
    'escritorio',
    'decoración',
    'decoracion',
    'decoración del hogar',
    'mejoras del hogar',
    'interior',
  ],
  gifts: [
    'regalo',
    'presente',
    'cumpleaños',
    'navidad',
    'aniversario',
    'flores',
    'tarjeta',
  ],
  services: [
    'plomero',
    'electricista',
    'técnico',
    'contratista',
    'reparación',
    'mantenimiento',
    'servicio',
    'pintor',
    'carpintero',
    'cerrajero',
    'limpieza',
    'instalación',
    'aire acondicionado',
    'reparación electrodomésticos',
  ],
  shoes: ['zapatos', 'calzado', 'zapatillas', 'tenis', 'botas', 'sandalias'],
  books: ['libro', 'libros', 'librería', 'revista'],
  toys: ['juguetes', 'juguetería', 'peluche', 'muñeca'],
  mall: ['centro comercial', 'plaza', 'galería comercial'],
  market: ['mercado', 'mercado local', 'tianguis', 'mercadillo'],
  department: ['tienda departamental'],
  jewelry: [
    'joyería',
    'anillo',
    'collar',
    'pulsera',
    'reloj',
    'accesorios',
    'bolsos',
    'cartera',
    'billetera',
    'cinturón',
    'bufanda',
    'sombrero',
  ],
  cosmetics: [
    'cosméticos',
    'maquillaje',
    'belleza',
    'perfume',
    'cuidado de la piel',
    'cuidado del cabello',
    'salón',
    'spa',
  ],
  sports: ['deportes', 'artículos deportivos', 'equipo deportivo', 'deportivo'],
  hardware: ['ferretería', 'herramientas'],
  garden: ['jardín', 'jardinería', 'plantas', 'flores', 'patio', 'césped'],
};

// Accommodation subcategory keywords
const accommodationSubcategories: AccommodationSubcategoryKeywords = {
  // ES common words only — brands live in `../shared/brands.ts`.
  hotel: [
    'hotel',
    'motel',
    'posada',
    'resort',
    'lodge',
    'suite',
    'hospedaje',
    'resort de playa',
    'resort de esquí',
    'resort spa',
    'todo incluido',
    'hospedaje carretera',
    'habitación',
    'cuarto',
    'alojamiento',
  ],
  airbnb: [
    'alquiler vacacional',
    'casa vacacional',
    'renta corta',
    'alojamiento temporal',
  ],
  apartment: [
    'apartamento',
    'depto',
    'departamento',
    'condominio',
    'estudio',
    'loft',
    'penthouse',
    'dúplex',
  ],
  house: [
    'casa',
    'hogar',
    'residencia',
    'villa',
    'cabaña',
    'bungalow',
    'chalet',
  ],
  rent: [
    'renta',
    'alquiler',
    'arriendo',
    'arrendamiento',
    'mensualidad',
    'pago de renta',
  ],
  hostel: [
    'hostal',
    'albergue',
    'mochilero',
    'dormitorio',
    'alojamiento económico',
  ],
  camping: [
    'camping',
    'campamento',
    'tienda',
    'caravana',
    'autocaravana',
    'glamping',
  ],
};

// Healthcare subcategory keywords
const healthcareSubcategories: HealthcareSubcategoryKeywords = {
  // ES common words only — brand chains live in `../shared/brands.ts`.
  doctor: [
    'médico general',
    'medico general',
    'clínica',
    'clinica',
    'revisión',
    'revision',
    'medicina general',
    'cita con médico',
    'cita médica',
  ],
  pharmacy: [
    'farmacia',
    'droguería',
    'drogueria',
    'medicamento',
    'medicina',
    'receta',
    'prescripción',
    'prescripcion',
    'similares',
    'boticas',
  ],
  hospital: [
    'hospital',
    'emergencia',
    'urgencias',
    'cirugía',
    'cirugia',
    'operación',
    'operacion',
    'internación',
    'ambulancia',
    'sala de emergencias',
    'laboratorio',
    'análisis de sangre',
    'prueba médica',
    'diagnóstico',
  ],
  dental: [
    'dental',
    'dentista',
    'dientes',
    'ortodoncista',
    'ortodoncia',
    'limpieza dental',
    'brackets',
    'cita dentista',
  ],
  vision: [
    'visión',
    'vision',
    'ojos',
    'optometrista',
    'lentes',
    'lentes de contacto',
    'gafas',
    'anteojos',
    'examen de vista',
  ],
  specialist: [
    'especialista',
    'consulta especialista',
    'dermatólogo',
    'dermatologo',
    'cardiólogo',
    'cardiologo',
    'pediatra',
    'psiquiatra',
    'terapia',
    'salud mental',
    'psicólogo',
    'consejería',
    'sesión de terapia',
    'fisioterapia',
    'rehabilitación',
    'terapia ocupacional',
  ],
  insurance: [
    'seguro',
    'seguro médico',
    'seguro medico',
    'copago',
    'deducible',
    'cobertura',
    'prima de seguro',
    'póliza',
    'poliza',
  ],
  fitness: [
    'gimnasio',
    'membresía gimnasio',
    'membresia gimnasio',
    'centro fitness',
    'entrenamiento',
    'entrenador personal',
    'ejercicio',
    'bienestar',
    'centro de bienestar',
    'medicina alternativa',
    'acupuntura',
    'masaje',
  ],
  supplements: [...healthSupplementsKeywords, ...supplementsKeywords],
};

// Utilities subcategory keywords
const utilitiesSubcategories: UtilitiesSubcategoryKeywords = {
  // ES common words only — brand providers live in `../shared/brands.ts`.
  electricity: [
    'electricidad',
    'eléctrica',
    'electrica',
    'energía',
    'energia',
    'luz',
    'recibo de luz',
    'factura de luz',
  ],
  water: [
    'agua',
    'servicio de agua',
    'acueducto',
    'alcantarillado',
    'drenaje',
    'recibo de agua',
  ],
  gas: [
    'gas',
    'gas natural',
    'servicio de gas',
    'factura de gas',
    'recibo de gas',
    'calefacción',
    'calefaccion',
    'propano',
    'caldera',
    'radiador',
  ],
  internet: [
    'internet',
    'wifi',
    'banda ancha',
    'fibra',
    'cable',
    'proveedor internet',
    'mundo',
  ],
  phone: [
    'teléfono',
    'telefono',
    'móvil',
    'movil',
    'celular',
    'plan de datos',
    'factura telefónica',
    'recibo de celular',
    'línea móvil',
  ],
  trash: [
    'basura',
    'residuos',
    'reciclaje',
    'recolección',
    'recoleccion',
    'desechos',
    'recolección de basura',
  ],
  maintenance: [
    'mantenimiento',
    'administración',
    'administracion',
    'condominio',
    'reparaciones',
    'cuota',
  ],
  streaming: [
    'suscripción',
    'suscripcion',
    'servicio streaming',
    'cable',
    'televisión',
    'television',
    'tv por cable',
    'satélite',
    'satelite',
  ],
};

// Shopping context keywords for better subcategory detection
const shoppingContext: ShoppingContextKeywords = {
  clothes: ['moda', 'boutique'],
  electronics: ['tecnología', 'dispositivo'],
  furniture: ['hogar', 'decoración'],
  online: ['envío'],
  groceries: ['semanal', 'mensual', 'lista de compras'],
  services: ['arreglar', 'reparar', 'roto', 'instalar', 'mantenimiento'],
};

// ML model pattern keywords for fast matching
const mlPatterns: MLPatternKeywords = {
  expense: {
    transport: [
      // Public transport
      'autobus',
      'autobús',
      'bus',
      'camión',
      'colectivo',
      'micro',
      'guagua',
      'tren',
      'metro',
      'subte',
      'tranvía',
      'tranvia',
      'ferrocarril',
      'renfe',
      'ave',
      'cercanías',

      // Air travel
      'vuelo',
      'avión',
      'avion',
      'aeropuerto',
      'aerolínea',
      'aerolinea',
      'avianca',
      'latam',
      'aeromexico',
      'iberia',
      'volaris',
      'viva',
      'copa',

      // Ride services
      'uber',
      'cabify',
      'didi',
      'beat',
      'lyft',
      'taxi',
      'taxista',

      // Vehicle related
      'gasolina',
      'gasoil',
      'combustible',
      'nafta',
      'estacionamiento',
      'parqueo',
      'peaje',
      'parking',
      'garaje',

      // Travel & transportation
      'transporte',
      'vacaciones',
      'viaje',
      'viajes',
      'trayecto',
      'pasaje',
      'boleto',
      'billete',

      // Rental & maintenance
      'alquiler coche',
      'renta auto',
      'hertz',
      'avis',
      'budget',
      'enterprise',
      'mantenimiento',
      'mecánico',
      'taller',
    ],
    food: [
      // Restaurants & dining
      'restaurante',
      'comedor',
      'cafetería',
      'cafeteria',
      'bistró',
      'bistro',
      'asador',
      'parrilla',
      'cantina',
      'bar',
      'cervecería',

      // Meals
      'almuerzo',
      'cena',
      'comida',
      'desayuno',
      'merienda',
      'aperitivo',
      'tapas',
      'picada',

      // Coffee & beverages
      'café',
      'cafe',
      'starbucks',
      'juan valdez',
      'coffee',
      'té',
      'te',
      'bebida',

      // Fast food chains
      'mcdonalds',
      'burger king',
      'kfc',
      'subway',
      'pizza hut',
      'dominos',
      'papa johns',
      'taco bell',
      'pollo campero',
      'popeyes',
      'wendys',

      // Grocery stores
      'mercado',
      'supermercado',
      'comestibles',
      'abarrotes',
      'despensa',
      'carrefour',
      'mercadona',
      'oxxo',
      'soriana',
      'walmart',
      'chedraui',
      'aurrera',
      'dia',
      'lidl',
      'aldi',

      // Food delivery
      'rappi',
      'uber eats',
      'pedidos ya',
      'didi food',
      'delivery',
      'entrega',

      // Food types
      'pizza',
      'hamburguesa',
      'sushi',
      'tacos',
      'empanadas',
      'panadería',
      'heladería',
    ],
    entertainment: [
      // Movies & cinema
      'película',
      'pelicula',
      'cine',
      'cinemark',
      'cinemex',
      'cinepolis',
      'película',
      'film',

      // Streaming services
      'netflix',
      'spotify',
      'apple music',
      'youtube',
      'disney',
      'hbo',
      'amazon prime',
      'paramount',
      'star+',
      'dazn',
      'crunchyroll',
      'hulu',

      // Music & concerts
      'concierto',
      'música',
      'musica',
      'festival',
      'ticketmaster',
      'boletia',
      'stubhub',
      'entrada',

      // Gaming
      'juego',
      'videojuego',
      'playstation',
      'xbox',
      'nintendo',
      'steam',
      'epic',
      'gaming',
      'arcade',

      // Sports & fitness
      'gimnasio',
      'gym',
      'fitness',
      'yoga',
      'pilates',
      'crossfit',
      'natación',
      'deportes',
      'estadio',
      'partido',

      // Events & venues
      'teatro',
      'museo',
      'exposición',
      'evento',
      'parque',
      'zoológico',
      'acuario',

      // Nightlife
      'bar',
      'discoteca',
      'club',
      'pub',
      'karaoke',
      'bowling',
    ],
    shopping: [
      // General shopping
      'compras',
      'tienda',
      'centro comercial',
      'mall',
      'plaza',
      'mercado',

      // Online shopping
      'amazon',
      'mercado libre',
      'aliexpress',
      'ebay',
      'shein',
      'alibaba',
      'wish',
      'etsy',

      // Clothing stores
      'zara',
      'h&m',
      'forever21',
      'gap',
      'uniqlo',
      'mango',
      'bershka',
      'pull&bear',
      'primark',
      'ropa',
      'calzado',
      'zapatos',

      // Department stores
      'liverpool',
      'palacio de hierro',
      'sears',
      'falabella',
      'ripley',
      'paris',
      'corte inglés',

      // Electronics
      'apple',
      'samsung',
      'sony',
      'best buy',
      'media markt',
      'fnac',
      'electrónica',
      'tecnología',

      // Home & furniture
      'ikea',
      'home depot',
      'leroy merlin',
      'sodimac',
      'muebles',
      'decoración',

      // Beauty & cosmetics
      'sephora',
      'ulta',
      'sally beauty',
      'perfume',
      'cosméticos',
      'belleza',
    ],
    healthcare: [
      // Medical services
      'médico',
      'medico',
      'doctor',
      'clínica',
      'clinica',
      'hospital',
      'consultorio',
      'emergencia',
      'urgencias',

      // Specialists
      'dentista',
      'dental',
      'ortodoncista',
      'oftalmólogo',
      'dermatólogo',
      'pediatra',
      'ginecólogo',
      'cardiólogo',
      'psicólogo',
      'psiquiatra',

      // Pharmacy
      'farmacia',
      'droguería',
      'medicamento',
      'medicina',
      'receta',
      'vitaminas',
      'farmacias del ahorro',
      'farmacias guadalajara',
      'cruz verde',
      'cruz azul',

      // Health insurance
      'seguro médico',
      'seguro salud',
      'copago',
      'prima',
      'cobertura',

      // Tests & procedures
      'laboratorio',
      'análisis',
      'radiografía',
      'examen',
      'prueba',
      'vacuna',

      // Wellness
      'spa',
      'masaje',
      'terapia',
      'rehabilitación',
      'fisioterapia',
    ],
    utilities: [
      // Basic utilities
      'electricidad',
      'luz',
      'energía',
      'cfe',
      'endesa',
      'iberdrola',

      'agua',
      'acueducto',
      'servicio agua',

      'gas',
      'gas natural',
      'propano',
      'calefacción',

      // Communication
      'teléfono',
      'telefono',
      'celular',
      'móvil',
      'telcel',
      'movistar',
      'claro',
      'at&t',
      'vodafone',
      'orange',

      // Internet & cable
      'internet',
      'wifi',
      'cable',
      'fibra',
      'telmex',
      'totalplay',
      'izzi',
      'megacable',
      'sky',
      'directv',

      // Bills & services
      'factura',
      'recibo',
      'servicios',
      'mensualidad',
      'pago',

      // Other utilities
      'basura',
      'reciclaje',
      'mantenimiento',
      'administración',
      'condominio',
    ],
    accommodation: [
      // Rent & housing
      'alquiler',
      'renta',
      'arriendo',
      'arrendamiento',
      'mensualidad',
      'depósito',

      // Types of housing
      'apartamento',
      'departamento',
      'depto',
      'casa',
      'habitación',
      'cuarto',
      'piso',
      'vivienda',

      // Hotels & lodging
      'hotel',
      'motel',
      'hostal',
      'airbnb',
      'booking',
      'hospedaje',
      'alojamiento',

      // Real estate
      'inmobiliaria',
      'hipoteca',
      'préstamo hipotecario',
      'crédito hipotecario',

      // Housing expenses
      'mudanza',
      'muebles',
      'decoración',
      'reparaciones',
    ],
    education: [
      // Educational institutions
      'escuela',
      'colegio',
      'instituto',
      'universidad',
      'facultad',
      'campus',
      'academia',

      // Courses & programs
      'curso',
      'clase',
      'materia',
      'asignatura',
      'carrera',
      'maestría',
      'doctorado',
      'diplomado',
      'certificación',

      // Educational expenses
      'matrícula',
      'matricula',
      'colegiatura',
      'inscripción',
      'mensualidad escolar',
      'beca',

      // Learning materials
      'libro',
      'libros',
      'cuaderno',
      'material escolar',
      'útiles',
      'uniforme',

      // Training & development
      'capacitación',
      'entrenamiento',
      'taller',
      'seminario',
      'conferencia',
      'congreso',

      // Online learning
      'coursera',
      'udemy',
      'platzi',
      'edx',
      'khan academy',
      'duolingo',

      // Other education
      'guardería',
      'kinder',
      'preescolar',
      'primaria',
      'secundaria',
      'preparatoria',
      'bachillerato',
    ],
  },
  group: {
    friends: [
      // Social terms
      'amigos',
      'amigas',
      'amistad',
      'compañeros',
      'compañeras',
      'grupo',
      'pandilla',
      'banda',
      'panas',
      'parceros',
      'cuates',
      'carnales',
      'bros',
      'social',
      'reunirse',
      'círculo',
      'equipo',
      'camaradas',
      'colegas',
      'conocidos',

      // Activities
      'cena',
      'almuerzo',
      'brunch',
      'desayuno',
      'comida',
      'bebidas',
      'copas',
      'tragos',
      'fiesta',
      'party',
      'reunión',
      'salida',
      'juego',
      'juegos',
      'película',
      'cine',
      'concierto',
      'bar',
      'club',
      'karaoke',
      'bowling',

      // Time references
      'noche',
      'tarde',
      'fin de semana',
      'finde',
      'viernes',
      'sábado',
      'domingo',

      // Common friend group names
      'squad',
      'crew',
      'gang',
      'los chicos',
      'las chicas',
      'los panas',
      'la banda',
    ],
    family: [
      // Family members
      'familia',
      'padres',
      'padre',
      'madre',
      'papá',
      'mamá',
      'hermanos',
      'hermano',
      'hermana',
      'hijos',
      'hijo',
      'hija',
      'abuelos',
      'abuelo',
      'abuela',
      'nietos',
      'tíos',
      'tío',
      'tía',
      'primos',
      'primo',
      'prima',
      'sobrinos',
      'sobrino',
      'sobrina',
      'suegros',
      'cuñados',
      'yerno',
      'nuera',

      // Family terms
      'parientes',
      'familiares',
      'parentesco',
      'hogar',
      'casa',
      'reunión familiar',
      'cena familiar',
      'almuerzo familiar',

      // Family activities
      'navidad',
      'año nuevo',
      'pascua',
      'cumpleaños',
      'aniversario',
      'vacaciones',
      'feriado',
      'domingo',
      'comida',
      'asado',
      'barbacoa',
      'mercado',
      'supermercado',
      'compras',

      // Shared expenses
      'compartido',
      'gastos del hogar',
      'servicios',
      'cuentas',
      'facturas',
    ],
    couple: [
      // Relationship terms
      'pareja',
      'novio',
      'novia',
      'esposo',
      'esposa',
      'marido',
      'mujer',
      'cónyuge',
      'matrimonio',
      'casados',
      'comprometidos',
      'prometido',
      'prometida',
      'relación',
      'romántico',
      'romance',
      'amor',

      // Couple activities
      'cita',
      'date',
      'cena romántica',
      'aniversario',
      'san valentín',
      'luna de miel',
      'viaje de pareja',

      // Living together
      'juntos',
      'convivencia',
      'vivir juntos',
      'hogar',
      'apartamento',
      'casa',
      'mudanza',

      // Shared expenses
      'gastos compartidos',
      'cuentas',
      'facturas',
      'alquiler',
      'renta',
      'hipoteca',
      'servicios',
      'mercado',
      'compras',

      // Common couple names
      'mi amor',
      'mi vida',
      'cariño',
      'bebé',
      'bae',
    ],
    events: [
      // Celebrations
      'fiesta',
      'fiestas',
      'celebración',
      'festejo',
      'evento',
      'ocasión especial',

      // Specific events
      'boda',
      'bodas',
      'matrimonio',
      'casamiento',
      'cumpleaños',
      'cumple',
      'aniversario',
      'graduación',
      'grado',
      'bautizo',
      'bautismo',
      'comunión',
      'confirmación',
      'quinceañera',
      'sweet 16',

      // Professional events
      'conferencia',
      'congreso',
      'seminario',
      'convención',
      'simposio',
      'taller',
      'workshop',
      'reunión',
      'junta',

      // Entertainment events
      'concierto',
      'festival',
      'show',
      'espectáculo',
      'obra',
      'teatro',
      'partido',
      'juego',

      // Other events
      'baby shower',
      'despedida',
      'despedida de soltero',
      'despedida de soltera',
      'revelación de género',
      'recepción',
      'ceremonia',
      'gala',
      'cena de gala',
      'cocktail',
      'brindis',
    ],
    roommates: [
      // Living arrangements
      'compañeros de cuarto',
      'compañeros de piso',
      'compañeros de casa',
      'roomies',
      'roommates',
      'inquilinos',
      'cohabitantes',

      // Housing terms
      'apartamento',
      'depto',
      'departamento',
      'piso',
      'casa',
      'habitación',
      'cuarto',
      'residencia',
      'vivienda',

      // Shared expenses
      'alquiler',
      'renta',
      'arriendo',
      'compartido',
      'dividir',
      'split',
      'gastos compartidos',
      'cuentas compartidas',

      // Utilities
      'servicios',
      'electricidad',
      'luz',
      'agua',
      'gas',
      'internet',
      'wifi',
      'cable',
      'basura',

      // Household
      'limpieza',
      'aseo',
      'suministros',
      'comida',
      'mercado',
      'despensa',
      'productos de limpieza',

      // Living together
      'viviendo juntos',
      'convivencia',
      'hogar compartido',
      'casa compartida',
    ],
    trip: [
      // Travel terms
      'viaje',
      'viajes',
      'vacaciones',
      'turismo',
      'tour',
      'excursión',
      'travesía',
      'aventura',
      'expedición',
      'escapada',
      'getaway',

      // Trip types
      'luna de miel',
      'viaje de negocios',
      'viaje familiar',
      'viaje con amigos',
      'mochilero',
      'backpacking',
      'crucero',
      'road trip',
      'fin de semana',
      'puente',
      'feriado',

      // Destinations
      'destino',
      'playa',
      'montaña',
      'ciudad',
      'campo',
      'resort',
      'paraíso',
      'explorar',
      'conocer',
      'visitar',

      // Transportation
      'vuelo',
      'vuelos',
      'avión',
      'tren',
      'autobús',
      'auto',
      'rental',
      'alquiler de auto',

      // Accommodation
      'hotel',
      'hoteles',
      'hostal',
      'airbnb',
      'hospedaje',
      'alojamiento',
      'resort',
      'cabaña',

      // Trip expenses
      'comida',
      'comidas',
      'restaurante',
      'cena',
      'almuerzo',
      'desayuno',
      'tours',
      'entradas',
      'souvenirs',
      'recuerdos',
      'actividades',
      'excursiones',
    ],
    work: [
      // Work terms
      'trabajo',
      'laboral',
      'profesional',
      'empleo',
      'ocupación',

      // Workplace
      'oficina',
      'empresa',
      'compañía',
      'corporación',
      'corporativo',
      'negocio',
      'startup',
      'agencia',
      'firma',

      // Team
      'equipo',
      'equipo de trabajo',
      'departamento',
      'área',
      'división',
      'colegas',
      'compañeros de trabajo',
      'colaboradores',
      'staff',
      'personal',

      // Work activities
      'proyecto',
      'proyectos',
      'reunión',
      'junta',
      'meeting',
      'conferencia',
      'call',
      'videollamada',
      'presentación',
      'pitch',

      // Work meals
      'almuerzo de trabajo',
      'almuerzo de negocios',
      'comida de trabajo',
      'cena de negocios',
      'coffee break',
      'happy hour',
      'after office',

      // Work events
      'team building',
      'evento corporativo',
      'fiesta de empresa',
      'celebración',
      'navidad de empresa',
      'retiro',
      'workshop',

      // Work expenses
      'suministros',
      'materiales',
      'viáticos',
      'transporte',
      'taxi',
      'uber',
      'estacionamiento',
    ],
  },
};

// Food configuration
const foodConfig: FoodConfig = {
  obviousSubcategories: ['fruta', 'verduras', 'vegetales'],
  subcategoryPriority: ['pizza', 'hamburguesa', 'sandwich', 'sopa', 'cafe'],
};

const drinksConfig: DrinksConfig = {
  subcategoryPriority: [
    'cocktails',
    'beer',
    'wine',
    'spirits',
    'energy',
    'coffee',
    'tea',
    'juice',
    'soda',
    'water',
    'bar',
    'alcohol',
    'drinks',
  ],
};

// Transport configuration
const transportConfig: TransportConfig = {
  subcategoryPriority: [
    'alquiler',
    'vuelo',
    'aeropuerto',
    'taxi',
    'viaje_compartido',
    'auto',
    'bicicleta',
    'barco',
    'tren',
    'tranvia',
  ],
};

// Spanish stemming configuration
const stemming: StemmingConfig = {
  suffixes: [
    'ando',
    'iendo',
    'ado',
    'ido',
    's',
    'es',
    'ción',
    'mente',
    'miento',
  ],
};

// Complete Spanish language keyword configuration
export const esKeywords: LanguageKeywords = {
  transport: transportKeywords,
  transportSubcategories,
  foodSubcategories: foodSubcategories as FoodSubcategoryKeywords,
  drinksSubcategories,
  entertainmentSubcategories,
  shoppingSubcategories,
  accommodationSubcategories,
  healthcareSubcategories,
  utilitiesSubcategories,
  context: contextKeywords,
  shoppingContext,
  foodConfig,
  drinksConfig,
  transportConfig,
  mlPatterns,
  stemming,
};
